/**
 * `V1xPlaceholderCard` — the honesty-seam card for V1.x-only surfaces.
 *
 * The compliance routes (`/compliance/gst-returns`, `/compliance/tds`) match the rich prototype
 * chrome which surfaces features (ITC reconciliation, Form-16A PDFs,
 * deposit timelines, LDC roster, GSTR-1/9 cards) that V1.0 doesn't ship.
 * Rather than hide those sections, we render a dashed-border card that
 * shows the title + a one-line description of *what* will land and *why*
 * it's deferred — same honesty pattern as the `unsupportedCodes` rows in
 * the GSTR-3B breakdown.
 *
 * Spec: prototype/compliance/gst-returns.html + prototype/compliance/tds.html — the rich
 * baseline. AGENTS.md §"When you build a UI screen": V1.x bits stay
 * visible, not hidden, with a "Lands in V1.x" affordance.
 *
 * Why a shared component (§7 rule 17): both compliance routes already
 * use this shape. The second usage is when we extract — not when there
 * are five.
 */
import { Clock3 } from "lucide-react";

export interface V1xPlaceholderCardProps {
  /** Heading — usually a section title from the rich prototype. */
  title: string;
  /** One-line description of what the section will surface in V1.x. */
  description: string;
}

export function V1xPlaceholderCard({ title, description }: V1xPlaceholderCardProps) {
  return (
    <section className="bg-bg border border-dashed border-line-2 rounded-md p-5 flex items-start gap-3">
      <span className="inline-grid place-items-center w-9 h-9 rounded-sm bg-paper text-muted border border-line flex-shrink-0">
        <Clock3 size={16} strokeWidth={2} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <strong className="text-sm text-ink-2 font-semibold">{title}</strong>
          <span className="px-1.5 py-px rounded-sm bg-paper text-xs font-bold uppercase tracking-wider text-muted border border-line">
            V1.x
          </span>
        </div>
        <p className="mt-1 text-xs text-muted leading-relaxed">{description}</p>
      </div>
    </section>
  );
}
